import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { TrendingUp, TrendingDown, Loader2, Lightbulb, AlertTriangle, Sparkles, Calculator, Users, LineChart, Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import SalarySimulator from "./SalarySimulator";
import { ForecastRiders } from "./ForecastRiders";

export const sar = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })} ر.س`);
const PLAT_TONE = { "هنجرستيشن": "from-rose-500 to-orange-400", "كيتا شرائح": "from-yellow-400 to-amber-500", "كيتا طلبات": "from-amber-300 to-yellow-500" };

const Stat = ({ label, value, sub, testid, tone = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`mt-1 font-heading text-2xl font-extrabold tabular-nums ${tone}`}>{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
  </div>
);

const Scen = ({ s }) => (
  <div className="grid grid-cols-3 gap-1.5 text-center">
    {[["conservative", "متحفظ", "text-slate-600"], ["expected", "متوقع", "text-blue-700"], ["optimistic", "متفائل", "text-emerald-700"]].map(([k, l, c]) => (
      <div key={k} className={`rounded-lg ${k === "expected" ? "bg-blue-50 ring-1 ring-blue-200" : "bg-slate-50"} p-1.5`}>
        <p className="text-[10px] text-slate-500">{l}</p><p className={`text-sm font-bold tabular-nums ${c}`}>{sar(s[k])}</p>
      </div>
    ))}
  </div>
);

const PlatformCard = ({ p }) => {
  const diff = p.last_month_revenue ? ((p.scenarios.expected - p.last_month_revenue) / p.last_month_revenue) * 100 : null;
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`fc-platform-${p.platform}`}>
      <div className={`h-1.5 bg-gradient-to-l ${PLAT_TONE[p.platform] || "from-slate-400 to-slate-500"}`} />
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div><p className="font-heading font-bold text-slate-900">{p.platform}</p>
            <p className="text-[11px] text-slate-500">سعر الطلب من الفواتير: <b>{p.price_per_order ? `${p.price_per_order} ر.س` : "—"}</b>
              <span className="group relative ms-1 inline-flex cursor-help align-middle" tabIndex={0} data-testid={`fc-price-info-${p.platform}`}><Info className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
                <span className="pointer-events-none absolute bottom-full right-0 z-20 mb-1 hidden w-52 rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] font-normal leading-relaxed text-white shadow-lg group-hover:block group-focus:block">صافي الفاتورة بدون الضريبة ÷ عدد الطلبات</span></span></p></div>
          {diff != null && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${diff >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid={`fc-diff-${p.platform}`}>
            {diff >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{diff.toFixed(1)}% عن الشهر الماضي</span>}
        </div>
        <Scen s={p.scenarios} />
        {p.invoice_actual != null && <p className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800" data-testid={`fc-actual-${p.platform}`}>فاتورة الشهر مرفوعة: الإيراد الفعلي <b>{sar(p.invoice_actual)}</b></p>}
        {p.note && <p className="text-[11px] text-amber-700">{p.note}</p>}
        {p.has_daily && (
          <>
            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600 sm:grid-cols-3">
              <span>لين الآن: <b className="text-slate-900">{sar(p.revenue_to_date)}</b></span>
              <span>الطلبات: <b className="text-slate-900">{p.orders_to_date?.toLocaleString("en-US")}</b></span>
              <span>أيام مرفوعة: <b>{p.days_with_data}</b> · متبقي <b>{p.days_left}</b></span>
              <span>متوسط آخر 7 أيام: <b>{p.daily_avg_recent}</b></span>
              <span>مناديب نشطين: <b>{p.active_riders}</b></span>
              <span>متوسط الشهر: <b>{p.daily_avg_month}</b></span>
            </div>
            {p.trend?.length > 1 && <div className="h-16" dir="ltr"><ResponsiveContainer><AreaChart data={p.trend}><XAxis dataKey="date" hide /><Tooltip formatter={(v) => [`${v} طلب`, ""]} labelFormatter={(l) => l} /><Area type="monotone" dataKey="orders" stroke="#2563eb" fill="#dbeafe" strokeWidth={2} /></AreaChart></ResponsiveContainer></div>}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-rose-50 p-2 text-[11px] text-rose-800" data-testid={`fc-lost-${p.platform}`}>الإيراد الضائع<br /><b>{sar((p.lost_revenue?.absence || 0) + (p.lost_revenue?.cancellations || 0))}</b><br />غياب {sar(p.lost_revenue?.absence)} · إلغاء {sar(p.lost_revenue?.cancellations)}</div>
              {p.what_if_plus2 > 0 && <div className="rounded-lg bg-indigo-50 p-2 text-[11px] text-indigo-800" data-testid={`fc-whatif-${p.platform}`}><Sparkles className="inline h-3 w-3" /> لو كل مندوب زاد طلبين يومياً<br /><b>+{sar(p.what_if_plus2)}</b> لباقي الشهر</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Overview = ({ d }) => {
  const diff = d.last_month_revenue ? ((d.compare_expected - d.last_month_revenue) / d.last_month_revenue) * 100 : null;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="الإيراد المتوقع آخر الشهر" value={sar(d.total.expected)} sub={`بين ${sar(d.total.conservative)} و ${sar(d.total.optimistic)}`} tone="text-blue-700" testid="fc-total-expected" />
        <Stat label="الإيراد المحقق لين الآن (حسب التقارير)" value={sar(d.revenue_to_date)} testid="fc-to-date" />
        <Stat label="إيراد الشهر الماضي (الفواتير)" value={sar(d.last_month_revenue)} sub={d.compare_platforms?.length ? `للمقارنة: ${d.compare_platforms.join("، ")}` : "لا توجد فواتير للشهر الماضي"} testid="fc-last-month" />
        <Stat label="التغير المتوقع" value={diff == null ? "—" : `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}%`} tone={diff == null ? "" : diff >= 0 ? "text-emerald-600" : "text-rose-600"} testid="fc-change" />
      </div>
      {d.recommendations?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="fc-recommendations">
          <p className="mb-2 flex items-center gap-1.5 font-bold text-amber-900"><Lightbulb className="h-4 w-4" /> توصيات</p>
          <ul className="space-y-1.5 text-sm text-amber-900">{d.recommendations.map((r, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />{r}</li>)}</ul>
        </div>
      )}
      <div className="grid gap-3 lg:grid-cols-3">{d.platforms.map((p) => <PlatformCard key={p.platform} p={p} />)}</div>
      <p className="text-[11px] text-slate-400">طريقة الحساب: سعر الطلب = صافي الفواتير السابقة (بدون الضريبة) ÷ طلباتها، وهنجرستيشن بشرائحك. الأيام اللي ما انرفع تقريرها تُقدّر بمتوسط آخر 7 أيام (المتحفظ ‎-10% والمتفائل ‎+10%).</p>
    </div>
  );
};

export default function RevenueForecast() {
  const [month, setMonth] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Riyadh" }).slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.revenueForecast(month).then(setD).catch((e) => { toast.error(e?.response?.data?.detail || "تعذّر حساب التوقع"); setD({ error: true }); }); }, [month]);
  const flagged = useMemo(() => (d?.riders || []).filter((r) => r.flags.length).length, [d]);
  return (
    <div className="space-y-4" data-testid="revenue-forecast">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-slate-900"><LineChart className="h-5 w-5 text-blue-600" /> الإيراد المتوقع</h2>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-40" data-testid="fc-month" />
      </div>
      <Tabs defaultValue="forecast" dir="rtl">
        <TabsList>
          <TabsTrigger value="forecast" data-testid="fc-tab-forecast"><TrendingUp className="me-1 h-4 w-4" /> التوقع</TabsTrigger>
          <TabsTrigger value="riders" data-testid="fc-tab-riders"><Users className="me-1 h-4 w-4" /> تحليل المناديب {flagged ? `(${flagged})` : ""}</TabsTrigger>
          <TabsTrigger value="sim" data-testid="fc-tab-sim"><Calculator className="me-1 h-4 w-4" /> محاكاة الراتب</TabsTrigger>
        </TabsList>
        <TabsContent value="forecast">{!d ? <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-300" /> : d.error ? null : <Overview d={d} />}</TabsContent>
        <TabsContent value="riders">{!d ? <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-300" /> : d.error ? null : <ForecastRiders riders={d.riders} />}</TabsContent>
        <TabsContent value="sim"><SalarySimulator /></TabsContent>
      </Tabs>
    </div>
  );
}
